import { FC } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import Table from '@components/table';

import { useTranslation } from 'react-i18next';
import { apiScheduleService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { getBillingStatusList, getFrequencyList, getNextPaymentDate } from '@utils/billing';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { useApiMutation } from '@hooks/api/useApiMutation';

import { EScheduleFrequency, EScheduleStatus, TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import Dropdown from '@components/common/dropdown';
import emptyIllustration from '@svg/illustrations/empty_schedule_bill.svg';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { formatDate, getScheduleStatusVariant } from '@utils/index';
import Status from '@components/common/status';
import Skeleton from '@components/common/skeleton';
import Info from '@components/common/info';
import { Icon } from '@components/icons';

interface IProps {
    onEdit: (data: TSchedule) => void;
    refetchPaymentsList: () => void;
    paymentsList: TSchedule[];
    isLoading: boolean;
    filter: { paymentFrequency?: EScheduleFrequency[]; status?: EScheduleStatus[] };
    onFilterChange: (val: {
        paymentFrequency?: EScheduleFrequency[];
        status?: EScheduleStatus[];
    }) => void;
}

const PaymentsTable: FC<IProps> = ({
    paymentsList,
    refetchPaymentsList,
    onEdit,
    filter,
    onFilterChange,
    isLoading
}) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'pay_bills.schedule_payments.table'
    });
    const { t: frequencyLocale } = useTranslation('sections', {
        keyPrefix: 'pay_bills.pay_bills.payment_details'
    });
    const { onOpen, changeModalData, onClose } = useModalContext();
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const navigate = useNavigate();

    const { handleRequest: handleCancelPayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deleteSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('cancel_schedule');
            refetchPaymentsList();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleRetrySchedule } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.retrySchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('retry_schedule');
            refetchPaymentsList();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleDeactivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.deactivateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('pause_schedule');
            refetchPaymentsList();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleActivatePayment } = useApiMutation({
        method: (id: string) => {
            return apiScheduleService.activateSchedule(id);
        },
        onSuccess: () => {
            requestSuccessLocale('activate_schedule');
            refetchPaymentsList();
        },
        onError: requestErrorLocale
    });

    const handlePauseResumePayment = (id: string, status: EScheduleStatus) => {
        if (status === EScheduleStatus.ACTIVE) {
            handleDeactivatePayment(id);
        } else {
            handleActivatePayment(id);
        }
    };

    const handleOpenCancelModal = (schedule: TSchedule) => {
        changeModalData({
            name: schedule.info.split('(')?.[0] || schedule.info
        });
        onOpen({
            modalId: modalIds.CANCEL_SCHEDULE,
            onSubmit: () => handleCancelPayment(schedule.id)
        });
    };

    const columns = [
        {
            label: t('payee'),
            key: 'payeeName',
            render: (_: string, data: TSchedule) => (
                <div className={styles.recipient}>
                    {data.status === EScheduleStatus.ERROR && (
                        <Info variant="right" content="Last payment attempt failed">
                            <Icon name="warning" />
                        </Info>
                    )}
                    {data.info}
                </div>
            ),
            width: 250
        },
        {
            label: t('frequency'),
            key: 'paymentFrequency',
            render: (value: EScheduleFrequency, data: TSchedule) =>
                getFrequencyList(frequencyLocale).find((item) => item.value === data.frequency)
                    ?.label,
            filterOptions: getFrequencyList(frequencyLocale)
        },
        {
            label: t('status'),
            key: 'status',
            render: (value: EScheduleStatus) => (
                <Status variant={getScheduleStatusVariant(value)}>
                    {
                        getBillingStatusList(frequencyLocale).find((item) => item.value === value)
                            ?.label
                    }
                </Status>
            ),
            filterOptions: getBillingStatusList(frequencyLocale)
        },
        {
            label: t('amount'),
            key: 'amount',
            render: (value: number) => <b>{`$${value.toFixed(2)}`}</b>
        },
        {
            label: 'Payments',
            key: 'remaining_payments',
            render: (value: number) => (value === -1 ? 'Infinite' : value)
        },
        {
            label: t('payment_date'),
            key: 'next_payment_at',
            render: (value: string, schedule: TSchedule) =>
                schedule.status !== EScheduleStatus.ACTIVE
                    ? '-'
                    : formatDate(
                          value ||
                              getNextPaymentDate({
                                  frequency: schedule.frequency,
                                  nextPaymentAt: schedule.next_payment_at,
                                  lastPaymentAt: schedule.last_payment_at
                              }),
                          'MM/DD/YYYY'
                      )
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_: undefined, data: TSchedule) => {
                return (
                    <div className={styles.table_actions}>
                        <Dropdown
                            items={[
                                {
                                    icon: 'open_eye',
                                    label: submitLocale('view_details'),
                                    onClick: () => {
                                        changeModalData({
                                            schedule: data,
                                            refetchSchedules: refetchPaymentsList,
                                            onEdit: (schedule: TSchedule) => {
                                                onClose();
                                                onEdit(schedule);
                                            }
                                        });
                                        onOpen({
                                            modalId: modalIds.VIEW_SCHEDULE
                                        });
                                    }
                                },
                                (data.status === EScheduleStatus.ACTIVE ||
                                    data.status === EScheduleStatus.INACTIVE) && {
                                    icon:
                                        data.status === EScheduleStatus.ACTIVE
                                            ? 'pause'
                                            : 'restart',
                                    label:
                                        data.status === EScheduleStatus.ACTIVE
                                            ? submitLocale('pause')
                                            : submitLocale('resume'),
                                    onClick: () => handlePauseResumePayment(data.id, data.status)
                                },
                                data.status === EScheduleStatus.ERROR && {
                                    label: submitLocale('retry'),
                                    icon: 'refresh',
                                    onClick: () => handleRetrySchedule(data.id)
                                },
                                {
                                    icon: 'trash',
                                    isRed: true,
                                    label: submitLocale('cancel'),
                                    onClick: () => handleOpenCancelModal(data)
                                }
                            ]}
                        />
                    </div>
                );
            }
        }
    ];

    return (
        <motion.div {...pageAnimation} className={styles.content}>
            {isLoading ? (
                <>
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                    <Skeleton className={styles.skeleton} />
                </>
            ) : paymentsList.length === 0 &&
              filter?.status?.length === 0 &&
              filter.paymentFrequency?.length === 0 ? (
                <div className={styles.empty}>
                    <img src={emptyIllustration} alt="empty payees" />
                    <p className={styles.title}>No bill payments have been scheduled yet</p>
                    <p className={styles.description}>
                        Bill payments you have scheduled will appear here
                    </p>
                    <Button variant="primary" onClick={() => navigate(routes.pay_bills_pay)}>
                        {submitLocale('schedule_bill_payment')}
                    </Button>
                </div>
            ) : (
                <Table
                    //@ts-ignore
                    columns={columns}
                    rows={paymentsList}
                    isActions
                    //@ts-ignore
                    filter={filter}
                    onFilterChange={(val) => {
                        onFilterChange({
                            ...filter,
                            ...(val?.paymentFrequency && {
                                //@ts-ignore
                                paymentFrequency: val.paymentFrequency as EScheduleFrequency[]
                            }),
                            ...(val?.status && {
                                //@ts-ignore
                                status: val.status as EScheduleStatus[]
                            })
                        });
                    }}
                />
            )}
            {paymentsList?.length === 0 &&
                (filter?.status?.length !== 0 || filter?.paymentFrequency?.length !== 0) &&
                !isLoading && (
                    <div className={styles.empty}>
                        <img src={emptyIllustration} alt="empty payees" />
                        <p className={styles.title}>No bill payments have been scheduled yet</p>
                        <p className={styles.description}>
                            Bill payments you have scheduled will appear here
                        </p>
                        <Button variant="primary" onClick={() => navigate(routes.pay_bills_pay)}>
                            {submitLocale('schedule_bill_payment')}
                        </Button>
                    </div>
                )}
        </motion.div>
    );
};

export default PaymentsTable;
